import { Fragment, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Field, Form, Formik, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Stack,
  Typography,
  useMediaQuery,
  Divider,
  Autocomplete,
  TextField,
  Grid,
  IconButton,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import theme from 'lib/theme'
import { contentLanguages } from 'lib/languages'
import { useCountryCodes } from 'hooks/useCountries'
import { usePutMasterProduct } from 'hooks/useMasterProducts'
import { useGetCategorisationInfo } from 'hooks/useCategories'
import { useGetInfiniteCategory } from 'hooks/useProducts'
import { useAlert } from 'providers/AlertProvider'
import { SelectDropdown } from 'components/SelectDropdown/SelectDropdown'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import Input from 'components/Input/Input'
import { InputWithDropdown } from 'components/InputWithDropdown/InputWithDropdown'
import { SaveBtn } from 'components/SaveBtn/SaveBtn'
import { BlueSwitch } from 'components/Switch/Switch.styled'
import { CategorisationInfo, CategoryField } from 'types/model/common'
import { useProductInfo } from './Product'
import { formatLangContentField, getCategoryFormat } from './utils'
import { LanguageSelector } from './LanguageSelector'

type FormikFieldType = {
  productType: string
  visibleToConsumers: boolean
  name: { options: { [lang: string]: string }; contentLanguage: string }
  description: {
    options: { [lang: string]: string }
    contentLanguage: string
  }
  brand: {
    options: { [lang: string]: string }
    contentLanguage: string
  }
  untranslatedBrand: string
  countryCode: { value: string; label: string }
  countryOfOrigin: {
    options: { [lang: string]: string }
    contentLanguage: string
  }
  categories: CategoryField[]
  percentageOfAlcohol: string | number
}

const productType = {
  FOOD_OR_BEVERAGE: 'FOOD_OR_BEVERAGE',
  FEE: 'FEE',
  DISCOUNT: 'DISCOUNT',
}

const Categorisation = ({
  categories,
  categorisationInfo,
}: {
  categories: CategoryField[]
  categorisationInfo: CategorisationInfo[]
}) => {
  const { t } = useTranslation()
  const { setFieldValue } = useFormikContext()
  const [tree, setTree] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<{
    label: string
    value: string
  } | null>(null)
  const [availableTrees, setAvailableTrees] = useState<
    { label: string; value: string }[] | null
  >(null)

  const { isFetching, categoryData } = useGetInfiniteCategory(tree)

  const handleDelete = (category: CategoryField) => {
    const newCategories = categories.filter(
      (tempCategory) => tempCategory.category.extId !== category.category.extId
    )
    setFieldValue('categories', [...newCategories])
  }

  const handleAdd = () => {
    if (selectedCategory) {
      const newCategory = {
        tree,
        category: {
          name: selectedCategory.label,
          extId: selectedCategory.value,
        },
      }
      const newCategories = [...categories]
      newCategories.push(newCategory)
      setFieldValue('categories', newCategories)
      setTree('')
      setSelectedCategory(null)
    }
  }

  useEffect(() => {
    setAvailableTrees(
      categorisationInfo.map((category) => {
        return { label: category.tree, value: category.tree }
      })
    )
  }, [categorisationInfo])

  return (
    <>
      <Typography variant='body2' pt={3}>
        {t('categorisation')}
      </Typography>
      <Grid container spacing={2} pl={1} mt={0} sx={{ alignItems: 'center' }}>
        {categories?.length > 0 &&
          categories.map((category) => {
            return (
              <Fragment key={category.tree + category.category.extId}>
                <Grid item xs={4}>
                  <Typography
                    variant='body2'
                    sx={{ color: theme.palette.text.secondary }}>
                    {category.tree}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: '18px' }}>
                    {category.category.name}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    aria-label={t('deleteCategory')}
                    onClick={() => handleDelete(category)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Fragment>
            )
          })}
        <Grid item xs={4} mt={2}>
          <SelectDropdown
            marginBottom='0px'
            selectOptions={availableTrees ?? []}
            label={t('tree')}
            selectedValue={tree}
            handleChange={(value) => {
              setTree(value)
              setSelectedCategory(null)
            }}
          />
        </Grid>
        <Grid item xs={6} mt={2}>
          <Autocomplete
            loading={isFetching}
            disablePortal
            disabled={tree.length === 0}
            noOptionsText={
              categoryData && categoryData?.length === 0
                ? t('noOptionsForLang')
                : t('noOptionsAutoComplete')
            }
            options={categoryData ?? []}
            onChange={(e, value, reason, details) => {
              if (reason === 'selectOption') {
                if (details) {
                  setSelectedCategory(
                    categoryData?.find(
                      (category) => category.value === details.option.value
                    ) || null
                  )
                }
              }
              if (reason === 'clear') {
                setSelectedCategory(null)
              }
            }}
            value={selectedCategory || null}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            getOptionLabel={(option) => option.label ?? ''}
            renderInput={(params) => (
              <TextField
                {...params}
                name='category'
                label={t('category')}
                InputLabelProps={{
                  shrink: true,
                  sx: { top: '-6px', left: '-4px' },
                }}
                variant='filled'
                sx={{
                  '& .MuiFilledInput-root': {
                    paddingTop: '2px',
                  },
                  marginBottom: '4px',
                }}
              />
            )}
            id='category'
          />
        </Grid>
        <Grid item xs={2} mt={2}>
          <IconButton
            sx={{
              '&:disabled': {
                '& .MuiSvgIcon-root': { fill: theme.palette.action.disabled },
              },
            }}
            aria-label={t('addCategory')}
            onClick={handleAdd}
            disabled={!tree || !selectedCategory?.label}>
            <AddIcon />
          </IconButton>
        </Grid>
      </Grid>
    </>
  )
}

const BaseInformation = ({
  values,
  countryCodes,
}: {
  values: FormikFieldType
  countryCodes: { label: string; value: string }[]
}) => {
  const { t } = useTranslation()
  const { setFieldValue } = useFormikContext()

  const inputWithDropdowns = [
    {
      label: t('name'),
      fieldName: 'name',
      fieldValue: values.name,
    },
    {
      label: t('desc'),
      fieldName: 'description',
      fieldValue: values.description,
    },
    {
      label: t('brand'),
      fieldName: 'brand',
      fieldValue: values.brand,
    },
  ]

  return (
    <>
      <Typography variant='body2' mt={3}>
        {t('baseInformation')}
      </Typography>
      <Stack spacing={3} ml={1} mt={3}>
        {inputWithDropdowns.map((input) => {
          return (
            <Fragment key={input.fieldName}>
              {input.fieldName === 'brand' && values.untranslatedBrand && (
                <Input
                  disabled
                  name='untranslatedBrand'
                  label={t('oldBrand')}
                  value={values.untranslatedBrand}
                  type='text'
                  variant='filled'
                  fullWidth
                  sx={{
                    '&.MuiFormControl-root': { marginBottom: theme.spacing(1) },
                  }}
                />
              )}
              <InputWithDropdown
                label={input.label}
                value={
                  input.fieldValue.options[input.fieldValue.contentLanguage]
                }
                selectedOption={input.fieldValue.contentLanguage}
                dropdownFieldName={`${input.fieldName}.contentLanguage`}
                inputFieldName={`${input.fieldName}.options`}
                inputOptions={input.fieldValue.options}
              />
            </Fragment>
          )
        })}
        <Autocomplete
          disablePortal
          noOptionsText={
            countryCodes.length === 0
              ? t('noOptionsForLang')
              : t('noOptionsAutoComplete')
          }
          renderOption={(props, option) => {
            return (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )
          }}
          options={countryCodes}
          onChange={(e, value, reason, details) => {
            if (reason === 'selectOption') {
              if (details) {
                setFieldValue('countryCode', details.option)
              }
            }
            if (reason === 'clear') {
              setFieldValue('countryCode', null)
            }
          }}
          value={values.countryCode}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label ?? ''}
          renderInput={(params) => (
            <TextField
              {...params}
              name='countryCode'
              label={t('countryOfOrigin')}
              InputLabelProps={{
                shrink: true,
                sx: { top: '-5px', left: '-4px' },
              }}
              variant='filled'
              sx={{
                '& .MuiFilledInput-root': {
                  paddingTop: theme.spacing(0.5),
                },
              }}
            />
          )}
          id='countryOfOrigin'
        />
        <InputWithDropdown
          label={t('countryOfOriginStatement')}
          value={
            values.countryOfOrigin.options[
              values.countryOfOrigin.contentLanguage
            ]
          }
          selectedOption={values.countryOfOrigin.contentLanguage}
          dropdownFieldName='countryOfOrigin.contentLanguage'
          inputFieldName='countryOfOrigin.options'
          inputOptions={values.countryOfOrigin.options}
        />
      </Stack>
    </>
  )
}

export const ProductDescriptiveInfo = () => {
  const { t } = useTranslation()
  const { setAlert } = useAlert()
  const {
    isLoading: isLoadingCategorisation,
    categorisationInfo,
    error: categorisationError,
  } = useGetCategorisationInfo()
  const { product, isLoading, error, masterProduct } = useProductInfo()
  const { masterProductId } = useParams()
  const { mutateAsync, isLoading: isLoadingPutRequest } = usePutMasterProduct(
    masterProductId as string
  )
  const { countryCodes, countryCodesLoading, countryCodesError } =
    useCountryCodes()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const productTypeOptions = [
    { label: t('foodOrBeverage'), value: productType.FOOD_OR_BEVERAGE },
    { label: t('fee'), value: productType.FEE },
    { label: t('discount'), value: productType.DISCOUNT },
  ]

  const initialValues = {
    productType: product.productType,
    visibleToConsumers: product.visibleToConsumers,
    name: {
      options: product.productName,
      contentLanguage: contentLanguages[0],
    },
    description: {
      options: product.description,
      contentLanguage: contentLanguages[0],
    },
    brand: {
      options: product.brand,
      contentLanguage: contentLanguages[0],
    },
    untranslatedBrand: product.untranslatedBrand,
    countryCode: product.countryCode
      ? countryCodes.find(
          (code: { value: string; label: string }) =>
            code.value === product.countryCode
        )
      : null,
    countryOfOrigin: {
      options: product.countryOfOrigin,
      contentLanguage: contentLanguages[0],
    },
    categories: product.categories
      ? getCategoryFormat(categorisationInfo, product.categories)
      : [],
    percentageOfAlcohol: product.percentageOfAlcohol || '',
    selectedLanguage: contentLanguages[0],
  }

  const handleContentLanguageChange = (
    value: string,
    setFieldValue: (field: string, value: string) => void
  ) => {
    const fieldsToChange = ['name', 'description', 'brand', 'countryOfOrigin']
    fieldsToChange.forEach((field) => {
      setFieldValue(`${field}.contentLanguage`, value)
    })
    setFieldValue('selectedLanguage', value)
  }

  const handleSubmit = async (values: Partial<FormikFieldType>) => {
    const masterProductCopy = { ...masterProduct }
    const temp = masterProductCopy.tradeItem.tradeItemInformation.extensions

    if (values.productType) {
      if (values.productType === productType.FOOD_OR_BEVERAGE) {
        temp.foodAndBeverageIngredientModule = {
          ...temp.foodAndBeverageIngredientModule,
          x_isFoodOrBeverage: true,
        }
        delete temp.dgExtraProductModule
      }
      if (values.productType !== productType.FOOD_OR_BEVERAGE) {
        temp.dgExtraProductModule = {
          ...temp.dgExtraProductModule,
          extraProduct: values.productType as 'FEE' | 'DISCOUNT',
        }
      }
    }

    if (values.visibleToConsumers !== undefined) {
      temp.dgPresentationModule = {
        ...temp.dgPresentationModule,
        productAbsoluteConsumerVisibility: values.visibleToConsumers,
      }
    }

    const formattedDescription = values.description
      ? formatLangContentField(values.description.options)
      : null
    const formattedNames = values.name
      ? formatLangContentField(values.name.options)
      : null
    const formattedBrand = values.brand
      ? formatLangContentField(values.brand.options)
      : null
    temp.tradeItemDescriptionModule.tradeItemDescriptionInformation = {
      ...temp.tradeItemDescriptionModule.tradeItemDescriptionInformation,
      ...(formattedNames && { descriptionShort: formattedNames }),
      ...(formattedDescription && {
        tradeItemDescription: formattedDescription,
      }),
      ...(formattedBrand && {
        brandNameInformation: {
          ...temp.tradeItemDescriptionModule.tradeItemDescriptionInformation
            .brandNameInformation,
          languageSpecificBrandName: formattedBrand,
        },
      }),
    }

    const formattedCountry = values.countryOfOrigin
      ? formatLangContentField(values.countryOfOrigin.options)
      : null
    temp.placeOfItemActivityModule = {
      ...temp.placeOfItemActivityModule,
      placeOfProductActivity: {
        ...temp.placeOfItemActivityModule?.placeOfProductActivity,
        ...(values.countryCode
          ? {
              countryOfOrigin: [
                {
                  countryCode: values.countryCode.value,
                },
              ],
            }
          : { countryOfOrigin: [] }),
        ...(formattedCountry && {
          countryOfOriginStatement: formattedCountry,
        }),
      },
    }

    const formattedCategories =
      values.categories && values.categories?.length > 0
        ? values.categories.map((category) => {
            return { extId: category.category.extId, treeName: category.tree }
          })
        : []

    temp.dgPresentationModule.presentationCategory = formattedCategories

    temp.alcoholInformationModule = {
      ...temp.alcoholInformationModule,
      alcoholInformation: {
        ...temp.alcoholInformationModule?.alcoholInformation,
        percentageOfAlcoholByVolume: values.percentageOfAlcohol
          ? Number(values.percentageOfAlcohol)
          : null,
      },
    }

    await mutateAsync(masterProductCopy, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }

  if (isLoading || countryCodesLoading || isLoadingCategorisation) {
    return <LoadingIndicator />
  }
  if (error || countryCodesError || categorisationError) {
    return <h1>Error</h1>
  }

  return (
    <Box maxWidth='500px'>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values) => handleSubmit(values)}>
        {({ setFieldValue, values, dirty }) => (
          <Form style={{ height: '100%' }}>
            <Stack mt={5} spacing={1} mb={3}>
              {contentLanguages?.length > 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: theme.spacing(2),
                    right: theme.spacing(2),
                    zIndex: 1000,
                  }}>
                  <LanguageSelector
                    selectedValue={values.selectedLanguage}
                    handleChange={handleContentLanguageChange}
                  />
                </Box>
              )}
              <SelectDropdown
                selectOptions={productTypeOptions}
                label={t('productType')}
                handleChange={(value: string) => {
                  setFieldValue('productType', value)
                }}
                selectedValue={values.productType}
              />
              <Box display='flex' alignItems='center' ml={theme.spacing(2)}>
                <Field
                  name='visibleToConsumers'
                  component={BlueSwitch}
                  value={values.visibleToConsumers}
                  checked={values.visibleToConsumers}
                  onChange={() => {
                    setFieldValue(
                      'visibleToConsumers',
                      !values.visibleToConsumers
                    )
                  }}
                  aria-label={t('toggleVisibleToConsumers')}
                />
                <Typography variant='body2' ml={1}>
                  {t('visibleToConsumers')}
                </Typography>
              </Box>
            </Stack>
            <Divider />
            <BaseInformation countryCodes={countryCodes} values={values} />
            <Categorisation
              categories={values.categories}
              categorisationInfo={categorisationInfo}
            />
            <Typography variant='body2' mt={3}>
              {t('alcoholicBeverageInformation')}
            </Typography>
            <Stack pb={8} ml={1} mt={3}>
              <Input
                label={t('percentageOfAlcoholPerVolume')}
                name='percentageOfAlcohol'
                type='text'
                variant='filled'
                fullWidth
              />
            </Stack>
            <Box
              mt={3}
              sx={{
                position: 'fixed',
                bottom: isMobile ? '0px' : theme.spacing(2),
                width: isMobile ? '92%' : '504px',
                zIndex: 1000,
              }}>
              <SaveBtn
                isValid
                dirty={dirty}
                showOverlay
                isLoadingRequest={isLoadingPutRequest}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
